// New collection, or edit one: a name and a colour (the Library mockup's colour bars). Uses the
// finish dialog's look; collections are shared by both people.
import { COLLECTION_COLOURS, type CollectionDto } from '@zinc/shared';
import { useState } from 'react';
import { useDeleteCollection, useSaveCollection } from '../../lib/library';

export function CollectionDialog(props: {
  collection?: CollectionDto;
  onClose: () => void;
  onSaved?: (c: CollectionDto) => void;
  onDeleted?: () => void;
}) {
  const editing = props.collection;
  const [name, setName] = useState(editing?.name ?? '');
  const [colour, setColour] = useState<string>(editing?.colour ?? COLLECTION_COLOURS[0]);
  const [confirming, setConfirming] = useState(false);
  const save = useSaveCollection();
  const remove = useDeleteCollection();
  const failed = save.isError || remove.isError;

  const submit = () => {
    if (!name.trim()) return;
    save.mutate(
      { id: editing?.id, name: name.trim(), colour },
      {
        onSuccess: (c) => {
          props.onSaved?.(c);
          props.onClose();
        },
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[rgba(31,30,28,0.35)] p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="collection-title"
      onClick={props.onClose}
    >
      <form
        className="flex w-full max-w-[420px] flex-col gap-3 rounded-[20px] border border-line bg-surface px-5 py-5"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <h2 id="collection-title" className="m-0 text-[20px] font-bold">
          {editing ? 'Edit collection' : 'New collection'}
        </h2>
        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-muted">
          Name
          <input
            autoFocus
            value={name}
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
            className="h-11 rounded-[12px] border border-line bg-surface px-3 text-[15px] font-normal text-ink outline-none focus:border-accent"
          />
        </label>
        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-muted">Colour</span>
          <div role="radiogroup" aria-label="Colour" className="flex gap-2">
            {COLLECTION_COLOURS.map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={colour === c}
                aria-label={`Colour ${c}`}
                onClick={() => setColour(c)}
                className={`flex h-11 w-11 items-center justify-center rounded-full border-2 bg-surface p-0 ${colour === c ? 'border-ink' : 'border-transparent'}`}
              >
                <span className="h-7 w-7 rounded-full" style={{ background: c }} />
              </button>
            ))}
          </div>
        </div>
        {failed && (
          <p role="alert" className="m-0 text-[13px] text-mistake">
            That didn’t save. Check your connection and try again.
          </p>
        )}
        {confirming ? (
          <div className="flex flex-col gap-2 rounded-[12px] bg-bg px-3 py-3">
            <p className="m-0 text-[14px]">
              Delete “{editing?.name}”? Its puzzles stay in the library.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="h-11 flex-1 rounded-[12px] border border-line bg-surface text-[15px] font-semibold text-ink"
              >
                Keep it
              </button>
              <button
                type="button"
                onClick={() =>
                  remove.mutate(editing!.id, {
                    onSuccess: () => {
                      props.onDeleted?.();
                      props.onClose();
                    },
                  })
                }
                className="h-11 flex-1 rounded-[12px] border-0 bg-mistake text-[15px] font-bold text-white"
              >
                Delete
              </button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2 pt-1">
            {editing && (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="h-12 rounded-[12px] border border-line bg-surface px-4 text-[15px] font-semibold text-mistake"
              >
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={props.onClose}
              className="h-12 flex-1 rounded-[12px] border border-line bg-surface text-[15px] font-semibold text-ink"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || save.isPending}
              className="h-12 flex-1 rounded-[12px] border-0 bg-accent text-[15px] font-bold text-white disabled:opacity-45"
            >
              {editing ? 'Save' : 'Create'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
